/* @flow */

export type LoadState = "INITIAL" | "LOADING" | "LOADED" | "FAILED";

export type State<T> = {
    loadState: LoadState;
    data: T | undefined | null;
};

export type Configuration<T, P> = {
    request: (x0: P) => Promise<T>;
    onSuccess: (x0: T) => void;
    onError: (error: any) => void;
    // a pure function that is there in case you want to parse the response
    // response is what is returned by the function "request", so usually json
    transformResponse?: (response: any) => T;
};

/**
 * This is a helper class to abstract away the task of holding a loading state in order
 * to not have it in the stores, where is creates a lot of unnecesary noise.
 *
 * RequestStateContainer is a good choice if you only have one get request (e.g. you load the customer once).
 *
 * The state is cached until you call "load" again, then the old state is forgotten.
 *
 * <T> is the data that is returned from your get request.
 * <P> is the data that is need to make the request happen (e.g. query parameters).
 */
class RequestStateContainer<T, P> {
    state: State<T>;
    request: (requestParam: P) => Promise<T>;
    onSuccess: (x0: T) => void;
    onError: (error: any) => void;
    lastRequest: Promise<T> | undefined | null;
    requestCounter: number;
    transformResponse: (response: any) => T;

    constructor(config: Configuration<T, P>) {
        this.state = {
            loadState: "INITIAL",
            data: null
        };
        this.request = config.request;
        this.onSuccess = config.onSuccess;
        this.onError = config.onError;
        this.transformResponse = config.transformResponse || ((x) => x);
        this.lastRequest = null;
        this.requestCounter = 0;
    }

    getState(): State<T> {
        return this.state;
    }

    load(requestParam: P): Promise<any> {
        this.state.loadState = "LOADING";
        this.requestCounter++;
        const currentRequestCounter = this.requestCounter;
        return this.request(requestParam)
            .then((response: T) => {
                if (currentRequestCounter !== this.requestCounter) {
                    return;
                }
                this.state.loadState = "LOADED";
                this.state.data = this.transformResponse(response);
                this.onSuccess(this.state.data);
            })
            .catch((error) => {
                if (currentRequestCounter !== this.requestCounter) {
                    return;
                }
                this.state.loadState = "FAILED";
                this.onError(error);
            });
    }
}

const exported = {
    RequestStateContainer
};

export default exported;
export { RequestStateContainer };
